import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  alpha,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../Logo';
import { motion } from 'framer-motion';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const navigate = useNavigate();
  const { login, error: authError, clearError, isAuthenticated, user } = useAuth();

  // Navigate after successful login and user is loaded
  useEffect(() => {
    if (loginSuccess && isAuthenticated && user) {
      console.log('✅ Login successful, user loaded, navigating...', { user: user.email });
      const from = new URLSearchParams(window.location.search).get('from') || '/';
      // Use setTimeout to ensure state is fully committed
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 100);
    }
  }, [loginSuccess, isAuthenticated, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();
    setLoginSuccess(false);

    // Validate inputs
    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('🔑 Attempting login...');
      const success = await login(email, password);
      console.log('🔑 Login result:', success);

      if (success) {
        setLoginSuccess(true);
        // Navigation will happen in useEffect after state updates
      } else {
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setIsSubmitting(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const error = localError || authError;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: alpha('#FFFFFF', 0.1),
          filter: 'blur(100px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: alpha('#D4AF37', 0.1),
          filter: 'blur(100px)',
        }}
      />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper 
          elevation={0} 
          sx={{ 
            p: 5, 
            maxWidth: 450, 
            width: '100%',
            borderRadius: 0,
            border: '2px solid',
            borderColor: 'divider',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Logo variant="full" size="small" animated />
            </Box>
            
            <Typography 
              variant="h4" 
              component="h1" 
              align="center"
              sx={{ 
                fontWeight: 700,
                letterSpacing: '0.02em',
                mb: 1,
              }}
            >
              WELCOME BACK
            </Typography>
            
            <Typography 
              variant="body2" 
              color="textSecondary" 
              align="center" 
              sx={{ 
                mb: 4,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              Sign in to your wardrobe
            </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          autoComplete="email"
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          autoComplete="current-password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTogglePassword}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <Box sx={{ mt: 2, mb: 2 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            variant="body2"
            sx={{ textDecoration: 'none' }}
          >
            Forgot Password?
          </Link>
        </Box>
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isSubmitting}
          sx={{ mt: 2, mb: 2 }}
        >
          {isSubmitting ? <CircularProgress size={24} /> : 'Sign In'}
        </Button>
        
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Don't have an account?{' '}
                <Link
                  component={RouterLink}
                  to="/register"
                  sx={{ textDecoration: 'none', fontWeight: 'medium' }}
                >
                  Sign Up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default LoginForm;